/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import axios from "axios";
import _ from "lodash";
import { toHeaderCase } from "js-convert-case";
import moment from "moment";
import { LoggerModule } from "@vlinder-web/logger-module-react";

import config from "../config/shelfDetail.config.json";

import { useShelfId } from "../provider";

import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { GET_SHELF_DETAIL } from "../graphql";
import { VApplication } from "../../../application";
import { AppNavigatorService } from "../../appNavigatorService";
import { capitalCase } from "change-case";
import { StorageModule } from "@vlinder-web/storage-module-react";

const log: any = LoggerModule?.getInstance();
const storageService = StorageModule.getInstance();

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

const getWhoamiSrv = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.AppNavigatorService") as AppNavigatorService;
};

export interface IShelfDetailMethods extends IPluginMethodMap {
  getShelfDetail(args: any): Promise<any>;
}

function isValidHttpUrl(string: string) {
  let url;

  try {
    url = new URL(string);
  } catch (_) {
    return false;
  }

  return url.protocol === "http:" || url.protocol === "https:";
}

export class ShelfDetailService implements IAgentPlugin {
  readonly methods: IShelfDetailMethods;

  constructor() {
    this.methods = {
      getShelfDetail: this.getShelfDetail.bind(this),
    };
  }

  private _getDistCenters = (data: any, distCenter: any) => {
    let _res: any = [];
    let firstHalf: any = [];

    if (data && Array.isArray(data) && data.length > 0) {
      if (data.length > 1 && !distCenter) {
        _res.push({
          label: "Select",
          value: "null",
          key: 0,
        });
      }
      data.map((item: any, index: number) => {
        if (item?.id === distCenter?.id) {
          firstHalf.push({
            label: item?.name ? capitalCase(item?.name) : item?.distCenterId,
            value: item?.id,
            key: index + 1,
          });
        } else {
          _res.push({
            label: item?.name ? capitalCase(item?.name) : item?.distCenterId,
            value: item?.id,
            key: index + 1,
          });
        }
      });
    }
    console.log(`_getDistCentersData : `, [...firstHalf, ..._res]);
    return [...firstHalf, ..._res];
  };

  private _getShelfStatus = (data: any, status: string) => {
    let _res: any = [];
    let firstHalf: any = [];
    if (data && Array.isArray(data) && data.length > 0) {
      data.map((item: any, index: number) => {
        if (item?.value === status) {
          firstHalf.push(item);
        } else {
          _res.push({
            ...item,
          });
        }
      });
    }
    console.log(`_getShelfStatus : `, [...firstHalf, ..._res], status);
    return [...firstHalf, ..._res];
  };

  async getShelfDetail(args: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    const { shelfId } = useShelfId();
    try {
      if (client) {
        const res = await client.query({
          query: GET_SHELF_DETAIL,
          variables: {
            shelfFilter: {
              _id: shelfId,
            },
            filter: {
              ...args?.criteria,
              shelf__in: shelfId,
              deliveryStatus__nin: "Delivered",
            },
            distFilter: {
              band__in:
                storageService.get("bandConfig", "local")?.band?.appCardId ??
                null,
            },
          },
          fetchPolicy: "network-only",
        });
        if (res?.data?.getShelf) {
          log.success(
            `gpt shelf data in shelfDetail screen :`,
            storageService.get("bandConfig", "local")
          );
          const _isMoveToShelfEnabled =
            res?.data?.getMetadata?.isMoveToShelfEnabled;
          let _templateData = this.formatShelfBoxes(
            res?.data?.getBoxes?.content
          );

          console.log(`row data`, _templateData);
          let _shelf = res?.data?.getShelf;
          return {
            //logoSrc: "https://i.ibb.co/y5kGMjy/store-1.png",
            boxes: res?.data?.getBoxes?.content,
            isMoveToShelfEnabled: _isMoveToShelfEnabled,
            title: this.formatTitle(_shelf),
            subtitle: this.formatSubTitle(_shelf),
            listItems: this.formatAttributeItems(
              _shelf,
              res?.data?.getBoxes?.count
            ),
            appCardList: _templateData,
            isBoxEmpty: res?.data?.getBoxes?.content?.length <= 0,
            activePage: 1,
            totalCount: res?.data?.getBoxes?.count,
            totalPages: 1,
            shelfName: _shelf?.name,
            shelfId: _shelf?.shelfId,
            shelfBand:
              storageService.get("bandConfig", "local")?.band?.appCardTitle ??
              null,
            buttons: [
              {
                variant: "primary",
                title: "Update Shelf",
                key: "editShelf",
              },
              {
                variant: "primary",
                title: "Print Shelf",
                key: "printShelf",
              },
            ],
            distCenterOptions: this._getDistCenters(
              res?.data?.getDistCents?.content,
              _shelf?.distCent
            ),
            shelfStatusOptions: this._getShelfStatus(
              config?.Shelf_Delivery_Status,
              _shelf?.shelfDeliveryStatus
            ),
            tweetyProps: this.formatTweetyProps(
              _shelf,
              res?.data?.getDistCents?.content
            ),
          };
        }
      }
    } catch (err: any) {
      console.log(`error in getShelfDetail`, err);
      return { isShelfEmpty: true };
      //return err;
    }
  }

  private formatShelfOptions = (shelves: any[]) => {
    let _res: any[] = [];
    shelves &&
      Array.isArray(shelves) &&
      shelves.length > 0 &&
      shelves.forEach((shelf: any) => {
        _res.push({
          id: shelf?.id,
          label: shelf?.name,
        });
      });
    return _res;
  };

  private formatTweetyProps = (shelf: any, distCents: any) => {
    let _res: any = config.TWEETY_PROPS;
    let _shelves: any[] =
      distCents && Array.isArray(distCents) && distCents.length > 0
        ? distCents.filter(
            (distCent: any) =>
              distCent?.distCenterId === shelf?.distCent?.distCenterId
          )
        : [];
    _shelves =
      _shelves && Array.isArray(_shelves) && _shelves.length > 0
        ? _shelves[0]?.band?.shelves
        : [];
    _shelves =
      _shelves && Array.isArray(_shelves) && _shelves.length > 0
        ? _shelves.filter(
            (shelfInfo: any) => shelfInfo?.shelfId !== shelf?.shelfId
          )
        : [];

    console.log(`_shelves`, _shelves);
    let _fields: any[] = _res?.fields;
    let _shelfOption = this.formatShelfOptions(_shelves);
    _fields = _fields.map((field: any) => {
      if (field?.key === "shelves") {
        return {
          ...field,
          compProps: {
            ...field?.compProps,
            options: _shelfOption,
          },
        };
      } else return field;
    });
    _res.fields = _fields;
    return _res;
  };

  private formatTitle(shelf: any) {
    let formatted = shelf?.name ? shelf?.name : "";
    return formatted;
  }

  private formatSubTitle(shelf: any) {
    let formatted = shelf?.shelfId ? shelf?.shelfId : "";
    return formatted;
  }

  private formatAttributeItems(shelf: any, boxesCount: number | string) {
    const formatted: any = {
      info: config?.attributeItems,
    };

    if (shelf) {
      const boxesCountindex = _.findIndex(
        formatted.info,
        (info: any) => info.key === "totalBoxes"
      );
      if (boxesCountindex > -1) {
        formatted.info[boxesCountindex].subTitle = boxesCount
          ? boxesCount
          : "0";
      }
      const shelfDeliveryStatusindex = _.findIndex(
        formatted.info,
        (info: any) => info.key === "shelfDeliveryStatus"
      );
      if (shelfDeliveryStatusindex > -1) {
        formatted.info[shelfDeliveryStatusindex].subTitle =
          shelf?.shelfDeliveryStatus;
      }
      const distCentindex = _.findIndex(
        formatted.info,
        (info: any) => info.key === "distCent"
      );
      if (distCentindex > -1) {
        formatted.info[distCentindex].subTitle = shelf?.distCent?.name;
      }
    }
    return formatted?.info;
  }

  private formatShelfBoxes(data: any) {
    let _appCard: any =
      Array.isArray(data) &&
      data.map((item: any) => {
        return {
          id: item?.id,
          appCardId: item?.masqID,
          appCardImage: item?.user?.image,
          appCardTitle: item?.user?.fullName,
          //appCardDescription: `${_data?.masqID} | ${_data?.costumeID}`,
          appCardList: [
            {
              name: "Masq ID",
              value: item?.masqID,
            },
            {
              name: "Costume ID",
              value: item?.costumeID,
            },
          ],
        };
      });

    return _appCard;
  }
}
